import {
    DesignerStateSendType,
    eventFilterMachine,
    EventFilterSendType,
    externalEventFilterMachine,
    projectFilterMachine,
} from '@/allocation-designer/allocationDesigner';
import {
    getSelectionFilterLabel,
    useDesignerFilterData,
} from '@/allocation-designer/composables/useDesignerFilterData';
import { useDesignerTimelineData } from '@/allocation-designer/composables/useDesignerTimelineData';
import { AllocationSelectionFilterStatus } from '@/components/AllocationSelectionFilter/AllocationSelectionFilter.vue';
import { AllocationGroupType } from '@/components/AllocationSelectionPanel/AllocationSelectionPanel.types';
import { Allocation, TIMELINE_AREA_TYPES, TIMELINE_ROOM_TYPES } from '@/components/Timeline/Timeline.types';
import {
    ExternalEventEventStatus,
    GetEventsInTimeRangeDocument,
    GetExternalEventsInTimeRangeDocument,
    GetProjectsInTimeRangeDocument,
    GetSectionsDataForAllocationDesignerTimelineDocument,
} from '@/generated/graphql';
import { $t } from '@/plugins/fluent';
import { insideRangeCheck } from '@/utils/date';
import { useQuery } from '@vue/apollo-composable';
import { useMachine } from '@xstate/vue';
import { DateTime } from 'luxon';
import { computed, ComputedRef, Ref, watch } from 'vue';

const DEFAULT_ACTIVE_EVENT_STATES = ['active-idea', 'active-option', 'active-fix', 'passive', 'completed', 'canceled'];
const DEFAULT_ACTIVE_PROJECT_STATES = ['active-idea', 'active-option', 'active-fix', 'completed'];
const DEFAULT_ACTIVE_EXTERNAL_EVENT_STATES = [
    ExternalEventEventStatus.Fix,
    ExternalEventEventStatus.Option,
    ExternalEventEventStatus.Completed,
];

// useDesignerFilterData marks the designer's scenario as unsaved on every change; the edit timeline has no scenario.
const noScenario: DesignerStateSendType = () => {};

function toIso(dateTime: DateTime): string {
    const iso = dateTime.toISO();
    if (!iso) {
        throw new Error(`Could not convert DateTime to ISO string: ${dateTime.invalidExplanation}`);
    }
    return iso;
}

function requireDateTime(dateTime: Ref<DateTime | undefined>): ComputedRef<DateTime> {
    return computed(() => {
        if (!dateTime.value) {
            throw new Error('The timeline range is not set yet');
        }
        return dateTime.value;
    });
}

/**
 * Allocations of all other events and projects in the displayed range, shown read-only and reduced
 * next to the edited event's own allocations, plus the designer's selection filters that decide which
 * of their variants are shown. Clicking an allocation opens its event or project in a new tab.
 */
export function useForeignAllocations(
    excludedEventId: Ref<string>,
    startDateTime: Ref<DateTime | undefined>,
    endDateTime: Ref<DateTime | undefined>,
) {
    const start = requireDateTime(startDateTime);
    const end = requireDateTime(endDateTime);
    const isRangeSet = computed(() => Boolean(startDateTime.value) && Boolean(endDateTime.value));

    // The results also contain the edited event itself. Written to the cache, they replace parts of the
    // event's own cached data, and Apollo then fetches that again.
    const queryOptions = () => ({
        enabled: isRangeSet.value,
        fetchPolicy: 'no-cache' as const,
        keepPreviousResult: true,
    });
    const rangeVariables = () => ({
        start: startDateTime.value && toIso(startDateTime.value),
        end: endDateTime.value && toIso(endDateTime.value),
    });

    const eventFilterQuery = useQuery(GetEventsInTimeRangeDocument, rangeVariables, queryOptions);
    const projectFilterQuery = useQuery(GetProjectsInTimeRangeDocument, rangeVariables, queryOptions);
    const externalEventFilterQuery = useQuery(GetExternalEventsInTimeRangeDocument, rangeVariables, queryOptions);
    const allocationsQuery = useQuery(
        GetSectionsDataForAllocationDesignerTimelineDocument,
        () => ({
            startDateTime: startDateTime.value && toIso(startDateTime.value),
            endDateTime: endDateTime.value && toIso(endDateTime.value),
            roomTypes: TIMELINE_ROOM_TYPES,
            areaTypes: TIMELINE_AREA_TYPES,
        }),
        queryOptions,
    );

    const eventFilter = useMachine(eventFilterMachine, {
        input: { type: 'Event', activeGroupStates: DEFAULT_ACTIVE_EVENT_STATES },
    });
    const projectFilter = useMachine(projectFilterMachine, {
        input: { type: 'Project', activeGroupStates: DEFAULT_ACTIVE_PROJECT_STATES },
    });
    const externalEventFilter = useMachine(externalEventFilterMachine, {
        input: { type: 'ExternalEvent', activeGroupStates: DEFAULT_ACTIVE_EXTERNAL_EVENT_STATES },
    });

    // Leaving the edited event out of its filter also keeps its allocations out of the foreign ones, since
    // useDesignerTimelineData only passes the variants the filters show.
    watch(eventFilterQuery.result, (data) => {
        if (!data) return;
        const allocationGroups = data.allocationGroups && {
            ...data.allocationGroups,
            nodes: data.allocationGroups.nodes.filter((event) => event.id !== excludedEventId.value),
        };
        eventFilter.send({ type: 'set.data', data: { ...data, allocationGroups }, start: start.value, end: end.value });
    });
    watch(projectFilterQuery.result, (data) => {
        if (!data) return;
        projectFilter.send({ type: 'set.data', data, start: start.value, end: end.value });
    });
    watch(externalEventFilterQuery.result, (data) => {
        if (!data) return;
        externalEventFilter.send({ type: 'set.data', data, start: start.value, end: end.value });
    });

    const eventFilterData = useDesignerFilterData(eventFilter.snapshot, eventFilter.send, noScenario, start, end);
    const projectFilterData = useDesignerFilterData(projectFilter.snapshot, projectFilter.send, noScenario, start, end);
    const externalEventFilterData = useDesignerFilterData(
        externalEventFilter.snapshot,
        externalEventFilter.send,
        noScenario,
        start,
        end,
    );

    function toFilterProps(
        type: AllocationGroupType,
        data: ReturnType<typeof useDesignerFilterData>,
        send: EventFilterSendType,
    ) {
        return computed(() => ({
            type,
            sections: data.sections.value,
            label: getSelectionFilterLabel(type),
            // Only ever one of the filter's statuses, but inferred as a plain string
            status: data.status.value as AllocationSelectionFilterStatus,
            allocationCount: data.visibleCount.value,
            plusCount: data.plusCount.value,
            minusCount: data.minusCount.value,
            undoChanges: data.undoChanges,
            toggleSectionIsExpanded: (sectionId: string) => send({ type: 'toggle.section.expand', sectionId }),
            toggleVisibility: data.toggleVisibility,
            makeMainVariantVisible: data.setMainVariantVisible,
            makeActiveVariantsVisible: data.setActiveVariantsVisible,
            toggleNonActiveVariants: data.toggleNonActiveVariants,
        }));
    }

    const externalEventFilterProps = toFilterProps('ExternalEvent', externalEventFilterData, externalEventFilter.send);
    const filters = {
        event: toFilterProps('Event', eventFilterData, eventFilter.send),
        project: toFilterProps('Project', projectFilterData, projectFilter.send),
        externalEvent: computed(() => ({
            ...externalEventFilterProps.value,
            toggleOverrideVisibility: externalEventFilterData.toggleOverrideVisibility,
        })),
    };

    const { allocations } = useDesignerTimelineData(
        allocationsQuery.result,
        eventFilterData.visibleIds,
        projectFilterData.visibleIds,
    );

    const foreignAllocations = computed<Allocation[]>(() =>
        allocations.value
            // Right after a range change, the kept result still holds the previous range's allocations
            .filter((allocation) =>
                insideRangeCheck(allocation.startDateTime, allocation.endDateTime, start.value, end.value),
            )
            .map((allocation) => ({
                ...allocation,
                label: allocation.groupLabel,
                // Reference allocations only matter for the edited event, so foreign bars don't show the icon
                allocationVariant: { ...allocation.allocationVariant, referenceAllocationId: null },
                variant: 'Faint' as const,
                labelStyle: 'Muted' as const,
                clickAction: {
                    type: 'Open-Url' as const,
                    target: allocation.linkTarget,
                    tooltipText: $t('open-allocation-group-in-new-tab', {
                        allocationGroupName: allocation.groupLabel,
                    }),
                },
            })),
    );

    const queries = [eventFilterQuery, projectFilterQuery, externalEventFilterQuery, allocationsQuery];
    // Also loading while the range is still unknown and the queries haven't started, so the page doesn't
    // render the timeline with only the event's own allocations first
    const loading = computed(() => queries.some((query) => !query.result.value && !query.error.value));
    const error = computed(() => queries.find((query) => query.error.value)?.error.value ?? null);

    return {
        foreignAllocations,
        filters,
        externalEventVisibleIds: externalEventFilterData.visibleIds,
        externalEventOverrideVisibilityIds: externalEventFilterData.overrideVisibilityFilterIds,
        loading,
        error,
    };
}
